/**
 * Final Settlement Report — PDF Export (client-side, jsPDF + autotable)
 *
 * Uses the shared branded PDF template infrastructure from pdfReportTemplate.js.
 *
 * Page 1 .... Summary dashboard (metadata + per-employee summary table)
 * Page 2+ ... Per-employee settlement breakdown (earnings, deductions,
 *             settlement calculation, AL encashment audit, employer costs)
 */

import toast from '../utils/toast';
import {
  ALIGN,
  DEFAULT_TABLE_STYLES,
  HEADER_FILL_COLOR,
  createBrandedPDF,
  drawMetadataBlock,
  drawSeparatorLine,
  addBrandedFooter,
  fmtCurrency,
  fmtDate,
  fmtDays,
} from './pdfReportTemplate';

const C = ALIGN.CURRENCY;
const N = ALIGN.NUMBER;
const T = ALIGN.TEXT;

function num(val) {
  if (val == null || val === '') return 0;
  const n = typeof val === 'string' ? parseFloat(val) : val;
  return isNaN(n) ? 0 : n;
}

function sumField(rows, key) {
  return rows.reduce((s, r) => s + num(r[key]), 0);
}

function pctLabel(rate) {
  const r = num(rate);
  if (r <= 0) return '';
  return r < 1 ? `${(r * 100).toFixed(1)}%` : `${r.toFixed(1)}%`;
}

// ---------------------------------------------------------------------------
// Summary page columns (subset of XLSX columns, optimised for landscape PDF)
// ---------------------------------------------------------------------------

const SUMMARY_COLUMNS = [
  { key: 'sn',               label: 'S/N',          halign: N, width: 8 },
  { key: 'staff_id',         label: 'Staff ID',     halign: T, width: 16 },
  { key: 'name',             label: 'Name',         halign: T, width: 38 },
  { key: 'department',       label: 'Department',   halign: T, width: 22 },
  { key: 'final_day',        label: 'Final Day',    halign: T, width: 18 },
  { key: 'total_normal_hours',label: 'Normal Hrs',  halign: N, fmt: fmtDays, width: 16 },
  { key: 'overtime_hours',   label: 'OT Hrs',       halign: N, fmt: fmtDays, width: 14 },
  { key: 'gross_pay',        label: 'Gross Pay',    halign: C, fmt: fmtCurrency },
  { key: 'total_deductions', label: 'Deductions',   halign: C, fmt: fmtCurrency },
  { key: 'net_pay',          label: 'Net Pay',      halign: C, fmt: fmtCurrency },
  { key: 'encashment_amount',label: 'AL Encash',    halign: C, fmt: fmtCurrency },
  { key: 'final_payment',    label: 'Final Payment',halign: C, fmt: fmtCurrency },
  { key: 'cost_to_company',  label: 'Cost to Co.',  halign: C, fmt: fmtCurrency },
];

// ---------------------------------------------------------------------------
// Per-employee breakdown builders
// ---------------------------------------------------------------------------

function buildEarningsRows(emp) {
  const rows = [];
  const p = emp;

  const basicEarned = num(p.basic_salary_earned);
  const basicMonthly = num(p.basic_salary_monthly);
  const daysWorked = num(p.working_days_worked);
  const dailyRate = num(p.daily_rate);
  const isProrated = basicEarned > 0 && basicEarned < basicMonthly;

  if (isProrated) {
    rows.push(['Basic Salary (prorated)', fmtCurrency(basicEarned),
      `${fmtCurrency(dailyRate)}/day × ${daysWorked % 1 === 0 ? daysWorked.toFixed(0) : daysWorked.toFixed(1)}d`]);
  } else {
    rows.push(['Basic Salary', fmtCurrency(basicMonthly), '']);
  }

  const allowances = p.allowance_breakdown;
  if (allowances && typeof allowances === 'object' && Object.keys(allowances).length > 0) {
    for (const [name, amount] of Object.entries(allowances)) {
      if (num(amount) > 0) rows.push([`  ${name}`, fmtCurrency(amount), '']);
    }
  } else if (num(p.total_allowances) > 0) {
    rows.push(['Allowances', fmtCurrency(p.total_allowances), '']);
  }

  const otDetails = p.calculation_metadata?.overtime_details || {};
  const weekdayHrs = num(otDetails.weekday_overtime_hours);
  const weekdayPay = num(otDetails.weekday_overtime_pay);
  const restDayOtHrs = num(otDetails.rest_day_overtime_hours);
  const restDayOtPay = num(otDetails.rest_day_overtime_pay);

  if (weekdayPay > 0) {
    rows.push(['Weekday OT', fmtCurrency(weekdayPay), `${weekdayHrs}h × ${fmtCurrency(num(p.overtime_hourly_rate))}/hr`]);
  }
  if (restDayOtPay > 0) {
    rows.push(['Rest Day OT', fmtCurrency(restDayOtPay), `${restDayOtHrs}h`]);
  }
  if (weekdayPay <= 0 && restDayOtPay <= 0 && num(p.overtime_pay) > 0) {
    rows.push(['Overtime', fmtCurrency(p.overtime_pay), `${num(p.overtime_hours)}h`]);
  }

  if (num(p.public_holiday_pay) > 0) rows.push(['PH Pay', fmtCurrency(p.public_holiday_pay), 'S88']);
  if (num(p.rest_day_pay) > 0) rows.push(['Rest Day Pay', fmtCurrency(p.rest_day_pay), '']);
  if (num(p.bonus_payments) > 0) rows.push(['Bonus (AW)', fmtCurrency(p.bonus_payments), '']);
  if (num(p.other_payments) > 0) rows.push(['Other Payments', fmtCurrency(p.other_payments), '']);

  return rows;
}

function buildDeductionsRows(emp) {
  const rows = [];
  const p = emp;

  if (num(p.cpf_employee_contribution) > 0) {
    const rateStr = pctLabel(p.cpf_employee_rate);
    rows.push([`CPF Employee${rateStr ? ` (${rateStr})` : ''}`, fmtCurrency(p.cpf_employee_contribution), '']);
  }
  if (num(p.income_tax_deduction) > 0) rows.push(['Income Tax', fmtCurrency(p.income_tax_deduction), 'IRAS']);
  if (num(p.shg_total_contribution) > 0) rows.push(['SHG Fund', fmtCurrency(p.shg_total_contribution), '']);
  if (num(p.ir21_deduction_amount) > 0) rows.push(['IR21', fmtCurrency(p.ir21_deduction_amount), 'IRAS']);

  if (num(p.leave_deduction_amount) > 0) {
    const breakdown = p.leave_deduction_breakdown;
    if (Array.isArray(breakdown) && breakdown.length > 0) {
      for (const item of breakdown) {
        const label = item.leave_type || item.description || 'Leave';
        const days = item.working_days_in_period || item.days_in_period;
        rows.push([label, fmtCurrency(item.deduction_amount || item.amount), days ? `${days}d` : '']);
      }
    } else {
      rows.push(['Leave Deductions', fmtCurrency(p.leave_deduction_amount), `${num(p.unpaid_leave_days)}d`]);
    }
  }

  if (num(p.ns_deduction_amount) > 0) rows.push(['NS Recovery', fmtCurrency(p.ns_deduction_amount), 'MINDEF']);
  if (num(p.work_permit_replacement_fee) > 0) rows.push(['WP Replacement Fee', fmtCurrency(p.work_permit_replacement_fee), 'MOM']);
  if (num(p.sponsorship_bond_deduction) > 0) rows.push(['Sponsorship Bond', fmtCurrency(p.sponsorship_bond_deduction), '']);

  const otherDed = num(p.other_deductions);
  const adjDeductions = p.adjustment_line_items?.deductions || p.other_deductions_breakdown?.line_items?.deductions || [];
  if (adjDeductions.length > 0) {
    for (const item of adjDeductions) {
      rows.push([item.description || 'Deduction', fmtCurrency(item.amount), '']);
    }
  } else if (otherDed > 0) {
    rows.push(['Other Deductions', fmtCurrency(otherDed), '']);
  }

  return rows;
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

/**
 * Export the Final Settlement Report as a branded PDF.
 *
 * @param {Object} data - Response from /final-settlement-report-data/
 *   data.summary.rows      – summary row dicts
 *   data.employees          – full EmployeePayrollSerializer payloads
 *   data.period_name, etc.
 */
export async function exportFinalSettlementPDF(data) {
  const summaryRows = data.summary?.rows || [];
  const employees = data.employees || [];

  if (!summaryRows.length && !employees.length) {
    toast.error('No final settlement data to export');
    return;
  }

  const periodLabel = data.period_name
    || (data.period_start_date
      ? `${fmtDate(data.period_start_date)} — ${fmtDate(data.period_end_date)}`
      : 'Unknown Period');

  const layout = await createBrandedPDF({
    title: 'Final Settlement Report',
    subtitle: `Period: ${periodLabel}`,
    orientation: 'landscape',
  });
  const { doc, autoTable: autoTableFn } = layout;

  // ── PAGE 1 — Summary Dashboard ──────────────────────────────────────

  const grandTotals = {
    employees: summaryRows.length,
    gross: sumField(summaryRows, 'gross_pay'),
    deductions: sumField(summaryRows, 'total_deductions'),
    net: sumField(summaryRows, 'net_pay'),
    encashment: sumField(summaryRows, 'encashment_amount'),
    final: sumField(summaryRows, 'final_payment'),
  };

  const metaLeft = [
    ['Period', periodLabel],
    ['Employees', String(grandTotals.employees)],
    ['Status', (data.status || 'calculated').replace(/_/g, ' ').toUpperCase()],
  ];
  const metaRight = [
    ['Total Gross Pay', fmtCurrency(grandTotals.gross)],
    ['Total Deductions', fmtCurrency(grandTotals.deductions)],
    ['Total Final Payment', fmtCurrency(grandTotals.final)],
  ];

  let y = drawMetadataBlock(doc, metaLeft, metaRight, layout);
  drawSeparatorLine(doc, y, layout.leftX, layout.rightX);
  y += 2;

  // Summary table
  const colStyles = {};
  SUMMARY_COLUMNS.forEach((col, i) => {
    colStyles[i] = { halign: col.halign };
    if (col.width) colStyles[i].cellWidth = col.width;
  });

  const summaryHead = [SUMMARY_COLUMNS.map(c => c.label)];
  const summaryBody = summaryRows.map((row, i) =>
    SUMMARY_COLUMNS.map(col => {
      if (col.key === 'sn') return i + 1;
      const raw = row[col.key];
      if (raw == null) return '-';
      if (col.key === 'final_day' && raw) return fmtDate(raw);
      if (col.fmt) return col.fmt(raw);
      return raw;
    })
  );

  // Grand total row
  summaryBody.push(
    SUMMARY_COLUMNS.map((col, ci) => {
      if (ci === 0) return { content: 'Total', colSpan: 4, styles: { fontStyle: 'bold', halign: 'right' } };
      if (ci >= 1 && ci <= 3) return undefined;
      if (col.key === 'final_day') return '';
      const val = sumField(summaryRows, col.key);
      return { content: col.fmt ? col.fmt(val) : val, styles: { fontStyle: 'bold' } };
    }).filter(v => v !== undefined)
  );

  const totalIdx = summaryBody.length - 1;

  autoTableFn(doc, {
    startY: y,
    head: summaryHead,
    body: summaryBody,
    ...DEFAULT_TABLE_STYLES,
    margin: layout.tableMargin,
    columnStyles: colStyles,
    didParseCell: (cellData) => {
      if (cellData.section === 'body' && cellData.row.index === totalIdx) {
        cellData.cell.styles.fillColor = [31, 41, 55];
        cellData.cell.styles.textColor = 255;
        cellData.cell.styles.fontStyle = 'bold';
      }
    },
  });

  // ── PAGE 2+ — Per-employee breakdown pages ─────────────────────────

  for (const emp of employees) {
    doc.addPage('landscape');
    let py = 14;

    const empName = `${emp.employee_first_name || ''} ${emp.employee_last_name || ''}`.trim() || emp.employee_staff_id;
    const passType = emp.employee_employment_pass_type || '';
    const endDate = emp.employee_employment_end_date;

    // Employee header
    doc.setFontSize(13);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(27, 42, 74);
    doc.text(empName.toUpperCase(), layout.leftX, py);

    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(100);
    const infoParts = [emp.employee_staff_id, emp.employee_department_name, emp.employee_trade_category, passType].filter(Boolean);
    doc.text(infoParts.join('  •  '), layout.leftX, py + 5);

    if (endDate) {
      doc.text(`Final Working Day: ${fmtDate(endDate)}`, layout.leftX, py + 10);
    }

    doc.setFontSize(8);
    doc.setFont(undefined, 'italic');
    doc.text('FINAL SETTLEMENT', layout.rightX, py, { align: 'right' });
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0);

    py += 14;
    drawSeparatorLine(doc, py, layout.leftX, layout.rightX);

    // Side-by-side tables: Earnings (left) | Deductions (right)
    const halfW = (layout.contentWidth - 6) / 2;
    const rightStart = layout.leftX + halfW + 6;

    const earningsRows = buildEarningsRows(emp);
    earningsRows.push([
      { content: 'Total Gross Pay', styles: { fontStyle: 'bold' } },
      { content: fmtCurrency(emp.gross_pay), styles: { fontStyle: 'bold' } },
      '',
    ]);
    if (num(emp.leave_encashment_amount) > 0) {
      const encDays = num(emp.leave_encashment_days);
      earningsRows.push([
        { content: `AL Encashment (${encDays.toFixed(2)}d)`, styles: { textColor: [21, 128, 61] } },
        { content: `+${fmtCurrency(emp.leave_encashment_amount)}`, styles: { textColor: [21, 128, 61] } },
        '',
      ]);
    }

    const deductionsRows = buildDeductionsRows(emp);
    deductionsRows.push([
      { content: 'Total Deductions', styles: { fontStyle: 'bold' } },
      { content: `-${fmtCurrency(emp.total_deductions)}`, styles: { fontStyle: 'bold', textColor: [220, 38, 38] } },
      '',
    ]);

    const sectionHeadStyle = {
      ...DEFAULT_TABLE_STYLES.headStyles,
      fontSize: 7,
      fillColor: [21, 128, 61],
    };

    autoTableFn(doc, {
      startY: py,
      head: [['Earnings', 'Amount', 'Notes']],
      body: earningsRows,
      ...DEFAULT_TABLE_STYLES,
      headStyles: sectionHeadStyle,
      margin: { left: layout.leftX, right: layout.pageW - layout.leftX - halfW },
      tableWidth: halfW,
      columnStyles: {
        0: { halign: T, cellWidth: halfW * 0.48 },
        1: { halign: C, cellWidth: halfW * 0.28 },
        2: { halign: T, cellWidth: halfW * 0.24, fontSize: 6, textColor: [120, 120, 120] },
      },
    });

    const earningsEndY = doc.lastAutoTable?.finalY || py + 30;

    const dedHeadStyle = {
      ...DEFAULT_TABLE_STYLES.headStyles,
      fontSize: 7,
      fillColor: [220, 38, 38],
    };

    autoTableFn(doc, {
      startY: py,
      head: [['Deductions', 'Amount', 'Notes']],
      body: deductionsRows,
      ...DEFAULT_TABLE_STYLES,
      headStyles: dedHeadStyle,
      margin: { left: rightStart, right: layout.pageW - rightStart - halfW },
      tableWidth: halfW,
      columnStyles: {
        0: { halign: T, cellWidth: halfW * 0.48 },
        1: { halign: C, cellWidth: halfW * 0.28 },
        2: { halign: T, cellWidth: halfW * 0.24, fontSize: 6, textColor: [120, 120, 120] },
      },
    });

    const dedEndY = doc.lastAutoTable?.finalY || py + 30;
    let sectionY = Math.max(earningsEndY, dedEndY) + 6;

    // Settlement Calculation + AL Encashment Audit + Employer Costs
    // Three narrow tables in a row (landscape gives us space)
    const thirdW = (layout.contentWidth - 12) / 3;

    // Settlement Calculation (left third)
    const settleRows = [
      ['Net Pay', fmtCurrency(emp.net_pay)],
    ];
    if (num(emp.total_reimbursements) > 0) {
      settleRows.push(['+ Reimbursements', `+${fmtCurrency(emp.total_reimbursements)}`]);
    }
    if (num(emp.leave_encashment_amount) > 0) {
      settleRows.push([`+ AL Encashment (${num(emp.leave_encashment_days).toFixed(2)}d)`, `+${fmtCurrency(emp.leave_encashment_amount)}`]);
    }
    if (num(emp.sponsorship_bond_deduction) > 0) {
      settleRows.push(['− Bond Deduction', `-${fmtCurrency(emp.sponsorship_bond_deduction)}`]);
    }
    settleRows.push([
      { content: 'Final Payment', styles: { fontStyle: 'bold' } },
      { content: fmtCurrency(emp.final_payment), styles: { fontStyle: 'bold' } },
    ]);

    autoTableFn(doc, {
      startY: sectionY,
      head: [['Settlement', 'Amount']],
      body: settleRows,
      ...DEFAULT_TABLE_STYLES,
      headStyles: { ...DEFAULT_TABLE_STYLES.headStyles, fontSize: 7, fillColor: [27, 42, 74] },
      margin: { left: layout.leftX },
      tableWidth: thirdW,
      columnStyles: {
        0: { halign: T, cellWidth: thirdW * 0.6 },
        1: { halign: C, cellWidth: thirdW * 0.4 },
      },
    });

    // AL Encashment Audit (middle third)
    const alMeta = emp.calculation_metadata?.leave_encashment || {};
    const alRows = [
      ['Accrued Days', fmtDays(alMeta.accrued_days)],
      ['Carried Forward', fmtDays(alMeta.carried_forward)],
      ['Consumed Days', fmtDays(alMeta.consumed_days)],
      [
        { content: 'Remaining (Encashed)', styles: { fontStyle: 'bold' } },
        { content: fmtDays(alMeta.encashment_days), styles: { fontStyle: 'bold' } },
      ],
    ];
    if (alMeta.gross_daily_rate) {
      alRows.push(['Gross Daily Rate', fmtCurrency(alMeta.gross_daily_rate)]);
    }
    if (alMeta.monthly_gross) {
      alRows.push(['Monthly Gross', fmtCurrency(alMeta.monthly_gross)]);
    }
    if (alMeta.work_week_type) {
      alRows.push(['Work Week', alMeta.work_week_type.replace(/_/g, ' ')]);
    }

    autoTableFn(doc, {
      startY: sectionY,
      head: [['AL Encashment Audit', '']],
      body: alRows,
      ...DEFAULT_TABLE_STYLES,
      headStyles: { ...DEFAULT_TABLE_STYLES.headStyles, fontSize: 7, fillColor: [21, 128, 61] },
      margin: { left: layout.leftX + thirdW + 6 },
      tableWidth: thirdW,
      columnStyles: {
        0: { halign: T, cellWidth: thirdW * 0.6 },
        1: { halign: C, cellWidth: thirdW * 0.4 },
      },
    });

    // Employer Costs (right third)
    const isCpfApplicable = ['Singapore Citizen', 'Permanent Resident'].includes(emp.employee_employment_pass_type);
    const empCostRows = [];
    if (isCpfApplicable && num(emp.cpf_employer_contribution) > 0) {
      const erRate = pctLabel(emp.cpf_employer_rate);
      empCostRows.push([`CPF Employer${erRate ? ` (${erRate})` : ''}`, fmtCurrency(emp.cpf_employer_contribution)]);
    }
    if (num(emp.sdl_amount) > 0) empCostRows.push(['SDL', fmtCurrency(emp.sdl_amount)]);
    if (num(emp.foreign_worker_levy) > 0) empCostRows.push(['FWL', fmtCurrency(emp.foreign_worker_levy)]);

    const employerTotal = num(emp.gross_pay) + num(emp.cpf_employer_contribution) + num(emp.sdl_amount) + num(emp.foreign_worker_levy);
    empCostRows.push([
      { content: 'Cost to Company', styles: { fontStyle: 'bold' } },
      { content: fmtCurrency(employerTotal), styles: { fontStyle: 'bold' } },
    ]);

    autoTableFn(doc, {
      startY: sectionY,
      head: [['Employer Costs', 'Amount']],
      body: empCostRows,
      ...DEFAULT_TABLE_STYLES,
      headStyles: { ...DEFAULT_TABLE_STYLES.headStyles, fontSize: 7, fillColor: [45, 95, 138] },
      margin: { left: layout.leftX + (thirdW + 6) * 2 },
      tableWidth: thirdW,
      columnStyles: {
        0: { halign: T, cellWidth: thirdW * 0.6 },
        1: { halign: C, cellWidth: thirdW * 0.4 },
      },
    });

    // Adjustment notes
    if (emp.adjustment_notes) {
      const lastY = doc.lastAutoTable?.finalY || sectionY + 40;
      doc.setFontSize(7);
      doc.setFont(undefined, 'italic');
      doc.setTextColor(180, 100, 20);
      doc.text(`Adjustment Notes: ${emp.adjustment_notes}`, layout.leftX, lastY + 6);
      doc.setTextColor(0);
      doc.setFont(undefined, 'normal');
    }
  }

  // ── Footer + Save ───────────────────────────────────────────────────

  addBrandedFooter(doc, layout);

  const periodSlug = (data.period_name || 'Unknown')
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9_]/g, '');
  doc.save(`Final_Settlement_Report_${periodSlug}.pdf`);
  toast.success('Final Settlement Report PDF exported');
}
